import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

/**
 * PageTransition — cinematic intertitle overlay that rises between routes.
 * Wes Anderson intertitle feel: ivory curtain, serif XXL chapter label fades in/out.
 *
 * On route change:
 *  1. A full-bleed ivory-encre curtain sweeps up from bottom
 *  2. The page section is swapped under the curtain
 *  3. The curtain sweeps off the top, revealing the new page
 *
 * Labels are derived from the route.
 */

const LABELS = {
    "/": { ch: "I", label: "Ouverture" },
    "/manifeste": { ch: "II", label: "Le Manifeste" },
    "/destinations": { ch: "III", label: "Le Territoire" },
    "/experiences": { ch: "IV", label: "Les Expériences" },
    "/parcours": { ch: "V", label: "Les Parcours" },
    "/adresses": { ch: "VI", label: "Les Adresses" },
    "/evenements": { ch: "VII", label: "L'Agenda" },
    "/balades": { ch: "VIII", label: "Les Balades" },
    "/scene": { ch: "IX", label: "La Scène" },
    "/carte": { ch: "X", label: "La Carte" },
    "/magazine": { ch: "XI", label: "Le Magazine" },
    "/festival": { ch: "XII", label: "Le Festival" },
    "/week-end": { ch: "XIII", label: "Week-end Builder" },
    "/guides": { ch: "XIV", label: "Les Guides" },
    "/loger": { ch: "XV", label: "Loger" },
    "/club": { ch: "XVI", label: "Le Cercle" },
    "/cote-secrete": { ch: "XVII", label: "Côte Secrète" },
};

function labelFor(pathname) {
    if (LABELS[pathname]) return LABELS[pathname];
    // Match prefixes (e.g. /destinations/ault → Le Territoire)
    for (const [prefix, v] of Object.entries(LABELS)) {
        if (prefix !== "/" && pathname.startsWith(prefix + "/")) {
            return { ch: v.ch, label: v.label };
        }
    }
    return { ch: "·", label: "Côte Perdue" };
}

export default function PageTransition({ children }) {
    const location = useLocation();
    const [showCurtain, setShowCurtain] = useState(false);
    const [currentLabel, setCurrentLabel] = useState(labelFor(location.pathname));

    // On pathname change, trigger curtain
    useEffect(() => {
        setCurrentLabel(labelFor(location.pathname));
        setShowCurtain(true);
        const t = setTimeout(() => setShowCurtain(false), 1200);
        return () => clearTimeout(t);
    }, [location.pathname]);

    return (
        <>
            {children}

            <AnimatePresence>
                {showCurtain && (
                    <motion.div
                        key={location.pathname}
                        className="fixed inset-0 z-[999] pointer-events-none"
                        data-testid="page-transition"
                        initial={{ y: "100%" }}
                        animate={{ y: "0%" }}
                        exit={{ y: "-100%" }}
                        transition={{
                            y: { duration: 0.7, ease: [0.83, 0, 0.17, 1] },
                        }}
                    >
                        <div className="w-full h-full bg-cp-encre cp-grain flex items-center justify-center relative overflow-hidden">
                            {/* Ornamental frame */}
                            <div className="absolute inset-8 md:inset-14 border border-cp-sable/20 pointer-events-none" />
                            <div className="absolute inset-10 md:inset-16 border border-cp-sable/10 pointer-events-none" />

                            {/* Chapter + label intertitle */}
                            <motion.div
                                className="text-center px-6"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ delay: 0.25, duration: 0.45 }}
                            >
                                <p className="cp-tiny text-cp-sable/80 mb-6 md:mb-10" style={{ letterSpacing: "0.4em" }}>
                                    CHAPITRE
                                </p>
                                <div className="cp-serif italic text-cp-sable text-[64px] md:text-[96px] lg:text-[120px] leading-none mb-6 md:mb-10">
                                    {currentLabel.ch}
                                </div>
                                <div className="w-16 md:w-24 h-px bg-cp-sable/50 mx-auto mb-6 md:mb-10" />
                                <h2 className="cp-serif text-cp-ivoire text-3xl md:text-5xl lg:text-6xl leading-tight">
                                    <em className="cp-display-italic">{currentLabel.label}</em>
                                </h2>
                            </motion.div>

                            {/* Corner CP signature */}
                            <div className="absolute bottom-10 md:bottom-14 right-10 md:right-20 text-cp-sable/40 cp-tiny"
                                 style={{ letterSpacing: "0.3em" }}>
                                CÔTE · PERDUE
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
